'use client'

import { useCallback, useEffect, useState } from 'react'
import { flushKitchenSyncQueue } from '@/shared/sync/flushKitchenSyncQueue'
import {
  getKitchenSyncQueueCount,
  KITCHEN_SYNC_CHANGED_EVENT,
} from '@/shared/sync/kitchenSyncQueue'

export function useKitchenSyncStatus() {
  const [online, setOnline] = useState(
    () => typeof navigator === 'undefined' || navigator.onLine,
  )
  const [pendingCount, setPendingCount] = useState(() => getKitchenSyncQueueCount())
  const [isFlushing, setIsFlushing] = useState(false)

  const refresh = useCallback(() => {
    setPendingCount(getKitchenSyncQueueCount())
  }, [])

  const flush = useCallback(async () => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) return 0
    setIsFlushing(true)
    try {
      const count = await flushKitchenSyncQueue()
      refresh()
      return count
    } finally {
      setIsFlushing(false)
    }
  }, [refresh])

  useEffect(() => {
    const onOnline = () => {
      setOnline(true)
      void flushKitchenSyncQueue().then(() => refresh())
    }
    const onOffline = () => setOnline(false)
    const onQueueChanged = () => refresh()

    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)
    window.addEventListener(KITCHEN_SYNC_CHANGED_EVENT, onQueueChanged)
    refresh()

    return () => {
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)
      window.removeEventListener(KITCHEN_SYNC_CHANGED_EVENT, onQueueChanged)
    }
  }, [refresh])

  return { online, pendingCount, isFlushing, flush, refresh }
}
